import { Alert, Button, Datepicker } from '@ibsys/kit';
import { FC, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  type C2EddyLoadRequest,
  useC2EddyCandidates,
  useRefreshC2EddyLoadResults,
  useStartC2EddyLoad,
} from '@/entities/integrations';
import { useHasPermission } from '@/entities/profile';
import { getApiErrorMessage } from '@/shared/api';
import { toastError, toastSuccess } from '@/shared/lib/utils/toast';

import { C2EddyCandidatesPreview } from './C2EddyCandidatesPreview';
import { C2EddyLoadRun } from './C2EddyLoadRun';

const PREVIEW_PAGE_SIZE = 20;

// Период закрытия обращений; обе границы календарь отдаёт как `yyyy-MM-dd` — дата без времени означает весь день
type C2EddyPeriod = {
  from?: string;
  to?: string;
};

type C2EddyPeriodForm = {
  period?: C2EddyPeriod;
};

/**
 * Поиск закрытых обращений HelpDeskEddy за период и загрузка всего найденного периода: превью
 * ничего не загружает, а загрузка идёт фоновым прогоном, ход и итог которого показываются здесь же.
 */
export const C2EddyPeriodLoad: FC = () => {
  const { t } = useTranslation(['integrations']);
  const canAdmin = useHasPermission('ADMIN_INTEGRATION_INFO');
  // Период последнего поиска: его показывает превью и его же загружает «Загрузить»
  const [searchedPeriod, setSearchedPeriod] = useState<C2EddyLoadRequest | undefined>(undefined);
  const [page, setPage] = useState(0);
  const [runId, setRunId] = useState<string | undefined>(undefined);

  const { control, handleSubmit } = useForm<C2EddyPeriodForm>({ defaultValues: { period: undefined } });
  const period = useWatch({ control, name: 'period' });

  const refreshLoadResults = useRefreshC2EddyLoadResults();
  const { mutateAsync: startLoad, isPending: isStarting } = useStartC2EddyLoad();
  const {
    data: candidates,
    isLoading: isLoadingCandidates,
    isFetching: isFetchingCandidates,
    error: candidatesError,
  } = useC2EddyCandidates(
    searchedPeriod && { ...searchedPeriod, limit: PREVIEW_PAGE_SIZE, offset: page * PREVIEW_PAGE_SIZE },
  );

  const isSamePeriod = (dateFrom?: string, dateTo?: string) =>
    searchedPeriod?.dateFrom === dateFrom && searchedPeriod?.dateTo === dateTo;

  const handleSearch = ({ period: nextPeriod }: C2EddyPeriodForm) => {
    const dateFrom = nextPeriod?.from;
    const dateTo = nextPeriod?.to;
    if (!dateFrom || !dateTo) return;

    // Повторный поиск того же периода — за свежим ответом: обращения могли закрыться или загрузиться
    if (isSamePeriod(dateFrom, dateTo)) refreshLoadResults();
    setSearchedPeriod({ dateFrom, dateTo });
    setPage(0);
  };

  const handleLoad = async () => {
    if (!searchedPeriod) return;

    try {
      const response = await startLoad(searchedPeriod);
      setRunId(response.runId ?? undefined);
      toastSuccess(t('integrations:c2Eddy.startSuccess'));
    } catch (error) {
      toastError(t('integrations:c2Eddy.startError'), { description: getApiErrorMessage(error) });
    }
  };

  const hasPeriod = Boolean(period?.from && period?.to);
  // Загружается найденный период: изменённый после поиска сначала ищется заново
  const isPeriodChanged = searchedPeriod !== undefined && !isSamePeriod(period?.from, period?.to);
  const canLoad =
    canAdmin &&
    !isStarting &&
    !isPeriodChanged &&
    !isFetchingCandidates &&
    !candidatesError &&
    (candidates?.total ?? 0) > 0;

  return (
    <div className="flex flex-col gap-4">
      <form onSubmit={handleSubmit(handleSearch)} className="flex flex-wrap items-end gap-3">
        <Datepicker
          mode="range"
          outputType="iso-date"
          control={control}
          name="period"
          label={t('integrations:c2Eddy.filter.period')}
          disabled={!canAdmin}
        />
        <Button type="submit" disabled={!canAdmin || !hasPeriod || isFetchingCandidates}>
          {t('integrations:c2Eddy.filter.search')}
        </Button>
      </form>

      {!searchedPeriod && (
        <div className="text-muted-foreground text-sm">{t('integrations:c2Eddy.candidates.needPeriod')}</div>
      )}

      {searchedPeriod && candidatesError && (
        <Alert
          type="error"
          title={t('integrations:c2Eddy.candidates.error')}
          description={getApiErrorMessage(candidatesError)}
        />
      )}

      {searchedPeriod && !candidatesError && (
        <C2EddyCandidatesPreview
          data={candidates}
          isLoading={isLoadingCandidates}
          page={page}
          pageSize={PREVIEW_PAGE_SIZE}
          onPageChange={setPage}
        />
      )}

      {searchedPeriod && (
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={handleLoad} disabled={!canLoad}>
            {t('integrations:c2Eddy.load')}
          </Button>
          <span className="text-muted-foreground text-sm">
            {t(isPeriodChanged ? 'integrations:c2Eddy.periodChanged' : 'integrations:c2Eddy.loadDescription')}
          </span>
        </div>
      )}

      {runId && <C2EddyLoadRun runId={runId} />}
    </div>
  );
};
